import { inject, Injectable, InjectionToken } from '@angular/core';
import { monacoColorThemes } from './monaco.helper';

export const MONACO_LOADER_OPTIONS = new InjectionToken<MonacoEditorLoaderOptions>('MONACO_LOADER_OPTIONS', {
  providedIn: 'root',
  factory: () => ({
    basePath: './monaco-editor-core/vs',
  }),
});

export interface MonacoEditorLoaderOptions {
  basePath: string;
  tsWorkerUrl?: string;
  sqlWorkerUrl?: string;
}

const DEFAULT_LANGUAGE = 'application/json';

@Injectable({
  providedIn: 'root',
})
export class MonacoEditorLoader {
  private loaded!: Promise<void>;

  private loaderLoaded!: Promise<void>;

  private readonly registeredWorkersMap = new Map<string, () => Worker>();

  options = inject(MONACO_LOADER_OPTIONS);

  async load(): Promise<void> {
    if (!this.loaded) {
      this.loaded = new Promise((resolve) => {
        const onGotAmdLoader = async () => {
          window.require(['vs/editor/editor.main'], async () => {
            this.provideMonacoWorkerUrl();
            this.allowPassingHtmlContentToTooltip();
            this.defineThemes();
            await import('./monaco.languages');
            resolve();
          });
        };

        // Load AMD loader if necessary
        if (!window.require) {
          this.loadMonacoLoader().then(onGotAmdLoader);
        } else {
          onGotAmdLoader();
        }
      });
    }

    return this.loaded;
  }

  async createDiffModel(
    original: string,
    modified: string,
    language?: string,
  ): Promise<monaco.editor.IDiffEditorModel> {
    await this.load();
    return {
      original: monaco.editor.createModel(original, language || DEFAULT_LANGUAGE),
      modified: monaco.editor.createModel(modified, language || DEFAULT_LANGUAGE),
    };
  }

  async retrieveStandaloneServicesModule(): Promise<StandaloneServicesModule> {
    await this.load();
    return new Promise((resolve) => {
      window.require(['vs/editor/standalone/browser/standaloneServices'], resolve);
    });
  }

  loadMonacoLoader() {
    if (!this.loaderLoaded) {
      const { basePath } = this.options;
      this.loaderLoaded = new Promise<void>((resolve) => {
        const loaderScript = document.createElement('script');
        loaderScript.src = `${basePath}/loader.js`;
        loaderScript.addEventListener('load', () => {
          window.require.config({
            paths: {
              vs: basePath,
            },
          });
          resolve();
        });
        document.body.appendChild(loaderScript);
      });
    }

    return this.loaderLoaded;
  }

  registerWorker(language: string, workerFactory: () => Worker) {
    this.registeredWorkersMap.set(language, workerFactory);
  }

  private allowPassingHtmlContentToTooltip() {
    window.require(['vs/base/common/marked/marked'], (m: unknown) => {
      const marked = m as MarkedModule;
      const originalParse = marked.parse;
      type Args = Parameters<typeof originalParse>;
      marked.parse = function (src: unknown, opt: { sanitize: boolean }) {
        // allow passing html content to hover tooltip
        opt.sanitize = false;
        // eslint-disable-next-line prefer-rest-params
        return originalParse.apply(this, arguments as unknown as Args);
      };
    });
  }

  private provideMonacoWorkerUrl() {
    window.MonacoEnvironment = {
      getWorker: (moduleId: string, label: string) => {
        const factory = this.registeredWorkersMap.get(label);
        if (factory) {
          return factory();
        }
        let url = `${this.options.basePath}/base/worker/workerMain.js`;
        if (label === 'typescript' || label === 'javascript') {
          url = '/assets/monaco-typescript/ts.worker.js';
        }
        if (label === 'sql') {
          url = '/assets/monaco-sql/sql.worker.js';
        }

        return new Worker(url, {
          name: label,
        });
      },
    };
  }

  private defineThemes() {
    monaco.editor.defineTheme(monacoColorThemes.git.light, {
      base: 'vs',
      inherit: true,
      rules: [
        { token: 'string.key.json', foreground: '#442d7c' },
        { token: 'string.value.json', foreground: '#a53161' },
        { token: 'delimiter.bracket.json', foreground: '#000000' },
        { token: 'delimiter.array.json', foreground: '#000000' },
        { token: 'delimiter.colon.json', foreground: '#000000' },
        { token: 'delimiter.comma.json', foreground: '#000000' },
        { token: 'number.json', foreground: '#86723b' },
        { token: 'keyword.json', foreground: '#442c7a' },
      ],
      colors: {},
    });
    monaco.editor.defineTheme(monacoColorThemes.git.dark, {
      base: 'vs-dark',
      inherit: true,
      rules: [
        { token: 'string.key.json', foreground: '#ae98ff' },
        { token: 'string.value.json', foreground: '#c43b73' },
        { token: 'delimiter.bracket.json', foreground: '#ffffff' },
        { token: 'delimiter.array.json', foreground: '#ffffff' },
        { token: 'delimiter.colon.json', foreground: '#ffffff' },
        { token: 'delimiter.comma.json', foreground: '#ffffff' },
        { token: 'number.json', foreground: '#a78f4a' },
        { token: 'keyword.json', foreground: '#00fcff' },
      ],
      colors: {},
    });
    monaco.editor.defineTheme(monacoColorThemes.custom.light, {
      base: 'vs',
      inherit: true,
      rules: [
        {
          token: 'string',
          foreground: '#442c7a',
        },
        { token: 'number', foreground: '#317159' },
        { token: 'comment', foreground: '#317159' },
        { token: 'predefined', foreground: '#86723b' },
        { token: 'operator', foreground: '#442c7a' },
        { token: 'keyword', foreground: '#442d7c' },
        { token: 'forbidden', foreground: '#db233c' },
        { token: 'type.identifier', foreground: '#86723b' },
        { token: 'column', foreground: '#1091a6' },
        { token: 'custom-variable', foreground: '#1091a6' },
      ],
      colors: {},
    });
    monaco.editor.defineTheme(monacoColorThemes.custom.dark, {
      base: 'vs-dark',
      inherit: true,
      rules: [
        {
          token: 'string',
          foreground: '#e35755',
        },
        { token: 'number', foreground: '#317159' },
        { token: 'comment', foreground: '#429777' },
        { token: 'predefined', foreground: '#86723b' },
        { token: 'operator', foreground: '#ffffff' },
        { token: 'keyword', foreground: '#ae98ff' },
        { token: 'forbidden', foreground: '#db233c' },
        { token: 'type.identifier', foreground: '#86723b' },
        { token: 'column', foreground: '#1091a6' },
        { token: 'custom-variable', foreground: '#1091a6' },
      ],
      colors: {},
    });
  }
}

interface StandalonePrettierParam {
  formatWithCursor: (
    model: unknown,
    config: unknown,
  ) => {
    formatted: {
      trim: () => string;
    };
    cursorOffset: number;
  };
}

interface MarkedModule {
  parse: (src: unknown, opt: { sanitize: boolean }) => unknown;
}

declare const window: {
  MonacoEnvironment: {
    getWorker: (moduleId: string, label: string) => Worker;
  };
  require: {
    (
      paths: string[],
      cb:
        | ((module: MarkedModule) => void)
        | Promise<void>
        | ((prettier: StandalonePrettierParam, parsersObj: unknown) => Promise<void>)
        | ((module: StandaloneServicesModule) => void),
    ): void;
    config: (options: { paths: Record<string, string> }) => void;
  };
  setTimeout: (cb: () => void, time: number) => number;
};

interface StandaloneServicesModule {
  StaticServices: {
    markerService: {
      get: () => {
        onMarkerChanged: (cb: () => void) => monaco.IDisposable;
      };
    };
  };
}
